import { useState } from 'react';
import {
  useAccountantAccess,
  useInviteAccountant,
  useMutateAccountantAccess,
  type AccessRow,
} from '@/api/hooks/useAccountantAccess';

function statusLabel(status: AccessRow['status']): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function expiryLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'expiry unavailable';
  return `Expires ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date)}`;
}

export function AccountantAccess() {
  const accessQuery = useAccountantAccess();
  const invite = useInviteAccountant();
  const mutateAccess = useMutateAccountantAccess();
  const [email, setEmail] = useState('');
  const [taxYear, setTaxYear] = useState('2026');
  const [revokeTarget, setRevokeTarget] = useState<AccessRow | null>(null);

  const submitInvitation = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    invite.mutate({ email: trimmedEmail, tax_year: Number(taxYear) });
  };

  const mutate = (row: AccessRow, action: 'resend' | 'cancel') => {
    mutateAccess.mutate({ grantId: row.grant_id, action, expectedUpdatedAt: row.updated_at });
  };

  return (
    <section className="space-y-6 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="accountant-access-heading">
      <div>
        <h2 id="accountant-access-heading" className="text-lg font-semibold">Accountant access</h2>
        <p className="mt-1 text-sm text-muted-foreground">Your accountant sees business income and expenses for the years you pick, nothing else.</p>
        <p className="mt-1 text-sm text-muted-foreground">Every change they make is listed under Business › Changes and can be reversed.</p>
      </div>

      <form onSubmit={submitInvitation} className="space-y-3 rounded-md border p-4">
        <h3 className="font-semibold">Invite an accountant</h3>
        <div className="grid gap-3 sm:grid-cols-[1fr_8rem_auto] sm:items-end">
          <div className="space-y-1.5">
            <label htmlFor="accountant-email" className="text-sm font-medium">Accountant email</label>
            <input id="accountant-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm" autoComplete="email" required />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="accountant-tax-year" className="text-sm font-medium">Tax year</label>
            <select id="accountant-tax-year" value={taxYear} onChange={(event) => setTaxYear(event.target.value)} className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </select>
          </div>
          <button type="submit" disabled={invite.isPending} className="min-h-11 rounded-md bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50">{invite.isPending ? 'Sending…' : 'Send invitation'}</button>
        </div>
        {invite.isError && <p role="alert" className="text-sm text-degraded-foreground">The invitation could not be sent. Try again.</p>}
        {invite.isSuccess && <p role="status" className="text-sm">Invitation sent.</p>}
      </form>

      {accessQuery.isPending && <p role="status" className="text-sm text-muted-foreground">Loading accountant access…</p>}
      {accessQuery.isError && <div role="alert" className="space-y-3 text-sm"><p>Accountant access could not be loaded.</p><button type="button" onClick={() => accessQuery.refetch()} className="min-h-11 rounded-md border px-4">Retry</button></div>}
      {!accessQuery.isPending && !accessQuery.isError && accessQuery.data?.access.length === 0 && <p className="text-sm text-muted-foreground">No accountants have access yet.</p>}

      {!accessQuery.isPending && !accessQuery.isError && accessQuery.data?.access.map((row) => (
        <article key={row.grant_id} className="space-y-3 rounded-md border p-4" aria-label={`${row.accountant_name} access`}>
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
            <div>
              <h3 className="font-semibold">{row.accountant_name}</h3>
              <p className="text-sm text-muted-foreground">{row.email_masked}</p>
            </div>
            <span className="rounded-full border px-2 py-1 text-xs font-semibold">{statusLabel(row.status)}</span>
          </div>
          <p className="text-sm text-muted-foreground">Business income and expenses only · {row.tax_year} · {expiryLabel(row.expires_at)}</p>
          <div className="flex flex-wrap gap-2">
            {row.status === 'pending' && <>
              <button type="button" onClick={() => mutate(row, 'resend')} className="min-h-11 rounded-md border px-4 text-sm">Resend invitation</button>
              <button type="button" onClick={() => mutate(row, 'cancel')} className="min-h-11 rounded-md border px-4 text-sm">Cancel invitation</button>
            </>}
            {row.status === 'active' && <button type="button" onClick={() => setRevokeTarget(row)} className="min-h-11 rounded-md border px-4 text-sm" aria-label={`Revoke ${row.accountant_name} access`}>Revoke access</button>}
            {row.status === 'revoked' && <button type="button" onClick={() => setEmail(row.email_masked)} className="min-h-11 rounded-md border px-4 text-sm">Invite again</button>}
          </div>
        </article>
      ))}

      {revokeTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="presentation">
          <div role="dialog" aria-modal="true" aria-labelledby="revoke-accountant-heading" aria-label="Revoke accountant access" className="w-full max-w-md space-y-4 rounded-xl border bg-card p-6 shadow-xl">
            <h2 id="revoke-accountant-heading" className="text-lg font-semibold">Revoke {revokeTarget.accountant_name} access?</h2>
            <p className="text-sm text-muted-foreground">They will no longer be able to open your business books. Saved changes remain in your account.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRevokeTarget(null)} className="min-h-11 rounded-md border px-4">Keep access</button>
              <button type="button" onClick={() => { mutateAccess.mutate({ grantId: revokeTarget.grant_id, action: 'revoke', expectedUpdatedAt: revokeTarget.updated_at }); setRevokeTarget(null); }} disabled={mutateAccess.isPending} className="min-h-11 rounded-md bg-destructive px-4 font-semibold text-destructive-foreground disabled:opacity-50">Revoke</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AccountantAccess;
